/**
 * User-scanner settings page — ``/settings/user-scanner``.
 *
 * Single Card with explicit Save / Reset buttons (matches the
 * Operational window pattern). Fields drive the injected
 * ``user-scanner-config.js`` running on the strichliste tablet:
 *
 * * Prefix — required leading characters on the scanned token. ``""``
 *   disables the prefix check (used by Snipe-IT setups where the QR
 *   encodes a bare numeric user id).
 * * Numeric only — when on, the scanner accepts digits only; when off,
 *   it accepts ``[a-zA-Z0-9_-]``.
 * * Min / Max length — accepted token-length bounds. Max length may be
 *   empty to mean "no upper bound"; when present it must be ≥ min.
 *
 * Changes take effect the next time the tablet reloads the page (the
 * scanner config is fetched once per page load).
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Space,
  Switch,
  Typography,
} from "antd";
import { NumInput as InputNumber } from "../../components/NumInput";

import {
  ApiError,
  type UserScannerConfigRow,
  api,
} from "../../api/client";

const { Title, Paragraph } = Typography;

interface UserScannerFormValues {
  prefix: string;
  numeric: boolean;
  min_len: number;
  max_len: number | null;
}

const HELP_TOOLTIP =
  "These values control how the strichliste tablet scanner recognizes " +
  "user QR codes. Changes take effect on next tablet reload.";

export const UserScannerSettingsPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [form] = Form.useForm<UserScannerFormValues>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const row: UserScannerConfigRow = await api.getUserScannerConfig();
      form.setFieldsValue({
        prefix: row.prefix ?? "",
        numeric: !!row.numeric,
        min_len: row.min_len,
        max_len: row.max_len,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    let values: UserScannerFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    // Cross-field guard: max_len (when present) must be >= min_len.
    // Client-side guard; server also validates (defense-in-depth at
    // app/routes/misc_settings.py — invalid_range 400).
    if (
      values.max_len !== null &&
      values.max_len !== undefined &&
      values.max_len < values.min_len
    ) {
      setError("Max length must be greater than or equal to min length.");
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await api.patchUserScannerConfig({
        prefix: values.prefix ?? "",
        numeric: !!values.numeric,
        min_len: values.min_len,
        max_len:
          values.max_len === null || values.max_len === undefined
            ? null
            : values.max_len,
      });
      setInfo("Saved. Reload the strichliste tablet to apply.");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card loading={loading}>
      <Title level={2} style={{ marginBottom: 8 }}>
        User scanner
      </Title>
      <Paragraph type="secondary">
        Configures the user-barcode scanner injected into the strichliste
        tablet. These values gate which scanned tokens are treated as user
        IDs: the prefix is required on every scan ({`""`} disables the
        prefix check), {`"numeric only"`} restricts the token to digits,
        and the length bounds bracket the accepted token length. Changes
        take effect the next time the tablet reloads.
      </Paragraph>
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {info && (
        <Alert
          type="success"
          message={info}
          closable
          onClose={() => setInfo(null)}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form<UserScannerFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          prefix: "",
          numeric: false,
          min_len: 1,
          max_len: null,
        }}
      >
        <Form.Item
          label="Prefix"
          name="prefix"
          tooltip={HELP_TOOLTIP}
          rules={[
            { max: 32, message: "Prefix is at most 32 characters." },
          ]}
        >
          <Input
            placeholder='e.g. "U-" (leave empty to disable)'
            allowClear
            maxLength={32}
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item
          label="Numeric only"
          name="numeric"
          valuePropName="checked"
          tooltip={HELP_TOOLTIP}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          label="Min length"
          name="min_len"
          tooltip={HELP_TOOLTIP}
          rules={[
            { required: true, message: "Min length is required." },
            {
              validator: (_, v) =>
                Number.isInteger(Number(v)) && Number(v) >= 1
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Min length must be a positive integer."),
                    ),
            },
          ]}
        >
          <InputNumber min={1} step={1} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          label="Max length"
          name="max_len"
          tooltip={`${HELP_TOOLTIP} Leave empty for no upper bound.`}
          rules={[
            {
              validator: (_, v) => {
                if (v === null || v === undefined || v === "") {
                  return Promise.resolve();
                }
                return Number.isInteger(Number(v)) && Number(v) >= 1
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        "Max length must be a positive integer or empty.",
                      ),
                    );
              },
            },
          ]}
        >
          <InputNumber
            min={1}
            step={1}
            placeholder="(no upper bound)"
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Space>
          <Button type="primary" loading={saving} onClick={() => void onSave()}>
            Save
          </Button>
          <Button onClick={() => void refresh()}>Reset</Button>
        </Space>
      </Form>
    </Card>
  );
};

export default UserScannerSettingsPage;
